/**
 * Storage utility functions for visual health analysis images
 */

const STORAGE_PREFIX = 'visual_analysis_';

export type BodyPartType = 'eyes' | 'lips' | 'tongue' | 'nails';

export interface VisualAnalysisImages {
    eyes?: string;
    lips?: string;
    tongue?: string;
    nails?: string;
}

/**
 * Save captured image to localStorage
 * @param type - Body part type (eyes, lips, tongue, nails)
 * @param base64Data - Base64 encoded image string
 * @throws Error if storage quota exceeded
 */
export const saveVisualAnalysisImage = (type: BodyPartType, base64Data: string): void => {
    try {
        const key = `${STORAGE_PREFIX}${type}`;
        localStorage.setItem(key, base64Data);
    } catch (error) {
        if (error instanceof DOMException && error.name === 'QuotaExceededError') {
            throw new Error('Storage quota exceeded. Please clear some space and try again.');
        }
        throw new Error(`Failed to save ${type} image: ${error}`);
    }
};

/**
 * Get a specific body part image from localStorage
 * @param type - Body part type
 * @returns Base64 encoded image string or null if not found
 */
export const getVisualAnalysisImage = (type: BodyPartType): string | null => {
    const key = `${STORAGE_PREFIX}${type}`;
    return localStorage.getItem(key);
};

/**
 * Get all visual analysis images from localStorage
 * @returns Object containing all captured images
 */
export const getVisualAnalysisImages = (): VisualAnalysisImages => {
    return {
        eyes: getVisualAnalysisImage('eyes') || undefined,
        lips: getVisualAnalysisImage('lips') || undefined,
        tongue: getVisualAnalysisImage('tongue') || undefined,
        nails: getVisualAnalysisImage('nails') || undefined,
    };
};

/**
 * Clear all visual analysis images from localStorage
 */
export const clearVisualAnalysisImages = (): void => {
    const types: BodyPartType[] = ['eyes', 'lips', 'tongue', 'nails'];
    types.forEach((type) => {
        const key = `${STORAGE_PREFIX}${type}`;
        localStorage.removeItem(key);
    });
};

/**
 * Check if all steps have been completed
 * @returns True if all 4 images have been captured
 */
export const isVisualAnalysisComplete = (): boolean => {
    const images = getVisualAnalysisImages();
    return !!(images.eyes && images.lips && images.tongue && images.nails);
};

/**
 * Get the current step based on captured images
 * @returns The next step number (1-4) or 5 if all complete
 */
export const getCurrentStep = (): number => {
    const images = getVisualAnalysisImages();
    if (!images.eyes) return 1;
    if (!images.lips) return 2;
    if (!images.tongue) return 3;
    if (!images.nails) return 4;
    return 5; // All complete
};